import type { ReactNode } from 'react';
import { useContext, useEffect, useRef, useState } from 'react';
import type { CreateHeyGenSessionRequest, CreateHeyGenSessionResponse } from '@trinity/chat/src/types/heygen';
import type { TTSConfig } from '@trinity/chat/src/types/tts-config';
import { ChatLoggerContext } from '../../containers/Chat/contexts.js';
import { useChatApiClient } from '../../containers/Chat/useApiClient.js';
import { LiveAvatarContextProvider } from './LiveAvatarContext';

type SessionStatus = 'idle' | 'loading' | 'ready' | 'error';

type Props = {
  /** HeyGen avatar ID configured for this Digital Twin. */
  avatarId: string;
  /** BCP-47 language code. Defaults to 'en'. */
  language?: string;
  ttsConfig: TTSConfig | null;
  children: ReactNode;
};

/**
 * LiveAvatarSessionManager
 *
 * Responsible for obtaining a HeyGen session token from the chat-api backend
 * (POST /v1/chat/live-avatar/session) and rendering the LiveAvatarContextProvider
 * once the token is available.
 *
 * This mirrors the role of LiveAvatarDemo in the HeyGen SDK demo, but:
 * - Hardcoded to LITE mode (voiceChat: false — we supply our own TTS audio)
 * - No Full / Push-to-Talk toggle UI
 * - Session token is fetched from our backend (which holds the HeyGen API key)
 *   rather than calling HeyGen directly from the browser
 *
 * Renders nothing while the session token is loading or if an error occurs.
 * The error is logged to the console for developer visibility.
 *
 * Must be rendered inside the ChatAPIProvider tree so that ChatApiClientContext
 * provides the authenticated Axios instance.
 */
export function LiveAvatarSessionManager({
  avatarId,
  language = 'en',
  ttsConfig,
  children,
}: Props): React.ReactElement | null {
  const apiClient = useChatApiClient();
  const loggerCtx = useContext(ChatLoggerContext);
  const loggerRef = useRef(loggerCtx);
  loggerRef.current = loggerCtx;
  const [sessionToken, setSessionToken] = useState<string | null>(null);
  const [status, setStatus] = useState<SessionStatus>('idle');

  // Track whether the component is still mounted so we don't set state after unmount.
  const mountedRef = useRef(true);
  useEffect(() => {
    mountedRef.current = true;
    return () => {
      mountedRef.current = false;
    };
  }, []);

  useEffect(() => {
    setStatus('loading');
    setSessionToken(null);

    const request: CreateHeyGenSessionRequest = {
      avatarId,
      language,
      // LITE mode: HeyGen does not run its own TTS. We generate audio on our
      // backend and drive the avatar with session.repeatAudio(base64Audio).
      mode: 'LITE',
    };

    apiClient.authenticatedClient
      .post<CreateHeyGenSessionResponse>('/chat/live-avatar/session', request)
      .then(response => {
        if (!mountedRef.current) return;
        setSessionToken(response.data.sessionToken);
        setStatus('ready');
      })
      .catch((error: unknown) => {
        if (!mountedRef.current) return;
        console.error(
          '[LiveAvatarSessionManager] Failed to fetch HeyGen session token:',
          error,
        );
        // Also capture to Sentry via ChatLoggerContext so token failures are
        // observable in production (console.error is invisible there).
        loggerRef.current.captureException(error);
        setStatus('error');
      });
  }, [avatarId, language, apiClient]);

  if (status !== 'ready' || sessionToken === null) {
    // Always render children even while loading or on error, so the overall
    // layout (sidebar, HeyGen panel placeholder, chat) remains visible.
    // LiveAvatar and HeyGenResponseBridge use useLiveAvatarContextOptional and
    // silently no-op when the context is not yet available.
    return <>{children}</>;
  }

  return (
    <LiveAvatarContextProvider sessionToken={sessionToken} ttsConfig={ttsConfig}>
      {children}
    </LiveAvatarContextProvider>
  );
}
